import { useEffect, useRef } from 'react';

const testimonials = [
  {
    quote:
      "Exceptional guidance from start to finish. The level of detail and market insight made all the difference in our Lake Tahoe purchase. Cam's local expertise in the Sierra Nevada region is truly unmatched — we could not have navigated this alpine market without this level of knowledge and professionalism.",
    author: 'M. & J. Harrington',
    location: 'South Lake Tahoe, CA',
    stars: 5,
    image:
      'https://public.readdy.ai/ai/img_res/1ff5a6fa9f3f6220c6a3831f55afc6bb.jpg',
    type: 'Buyer',
  },
  {
    quote:
      "Professional, responsive, and deeply knowledgeable about the Lake Tahoe market. Cam helped us find our dream second home with patience and precision. The entire experience felt genuinely personalized — like working with a trusted advisor who truly cared about finding us exactly the right property.",
    author: 'R. & S. Whitmore',
    location: 'North Lake Tahoe, CA',
    stars: 5,
    image:
      'https://readdy.ai/api/search-image?query=majestic%20luxury%20mountain%20estate%20stone%20facade%20exterior%20grand%20entrance%20tall%20pine%20trees%20Sierra%20Nevada%20California%20autumn%20evening%20golden%20light%20long%20driveway%20impressive%20architecture%20premium%20real%20estate%20photography%20dramatic%20sky%20photorealistic&width=900&height=640&seq=test2-2026&orientation=landscape',
    type: 'Second Home',
  },
  {
    quote:
      "Cam's strategic approach to listing our Tahoe property delivered results far beyond our expectations. Sold above asking price in record time. The whole process was seamless, stress-free, and handled with the level of discretion and sophistication you only get from a true luxury market specialist.",
    author: 'T. Beaumont',
    location: 'Alpine County, CA',
    stars: 5,
    image:
      'https://readdy.ai/api/search-image?query=private%20luxury%20resort-style%20infinity%20pool%20terrace%20overlooking%20Lake%20Tahoe%20California%20dusk%20twilight%20warm%20amber%20lighting%20loungers%20mountain%20silhouette%20water%20reflection%20gorgeous%20upscale%20residential%20property%20lifestyle%20photography%20cinematic%20atmosphere&width=900&height=640&seq=test3-2026&orientation=landscape',
    type: 'Seller',
  },
];

export default function Testimonials() {
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cards = cardsRef.current?.querySelectorAll('.testimonial-card');
    if (!cards) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.05 }
    );
    cards.forEach((card, i) => {
      const el = card as HTMLElement;
      el.style.opacity = '0';
      el.style.transform = 'translateY(60px)';
      el.style.transition = `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 0.15}s, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 0.15}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative z-10 py-28 px-4 md:px-8 rounded-t-[2.5rem] -mt-8" style={{ background: '#1E1C1A' }}>
      {/* Gold accent top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/35 to-transparent" />

      {/* Decorative background pattern */}
      <div
        className="absolute inset-0 opacity-[0.018] pointer-events-none"
        style={{
          backgroundImage:
            'url(https://readdy.ai/api/search-image?query=luxury%20dark%20abstract%20pattern%20subtle%20elegant%20texture%20background%20gold%20detail&width=1920&height=1080&seq=test-bg-2026&orientation=landscape)',
          backgroundSize: 'cover',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[10px] tracking-[0.55em] uppercase text-gold mb-4">Client Voices</p>
          <h2 className="font-serif text-5xl md:text-7xl text-white font-light">
            What <em className="italic text-gold">Clients Say</em>
          </h2>
          <div className="flex items-center justify-center gap-3 mt-5">
            <div className="w-16 h-px bg-gold/25" />
            <div className="w-1.5 h-1.5 rotate-45 bg-gold/50" />
            <div className="w-16 h-px bg-gold/25" />
          </div>
        </div>

        {/* Testimonial cards */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="testimonial-card flex flex-col rounded-2xl overflow-hidden"
              style={{
                background: '#2A2620',
                border: '1px solid rgba(255,255,255,0.07)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.4)',
              }}
            >
              {/* Property image */}
              <div className="relative h-56 overflow-hidden flex-shrink-0">
                <img
                  src={t.image}
                  alt={`Luxury real estate client — ${t.location}`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2A2620]/90 via-transparent to-transparent" />
                {/* Type badge */}
                <div className="absolute top-4 left-4 px-3 py-1.5 bg-gold rounded-full">
                  <span className="text-white text-[9px] tracking-[0.25em] uppercase font-medium">{t.type}</span>
                </div>
              </div>
              {/* Content */}
              <div className="p-7 flex flex-col flex-1">
                {/* Stars */}
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: t.stars }).map((_, si) => (
                    <i key={si} className="ri-star-fill text-gold text-sm" />
                  ))}
                </div>
                {/* Quote mark */}
                <i className="ri-double-quotes-l text-gold/20 text-5xl leading-none block mb-3" />
                <p className="text-warm-white/65 text-sm leading-relaxed mb-6 italic font-light flex-1">
                  {t.quote}
                </p>
                <div className="border-t border-white/8 pt-5">
                  <p className="font-serif text-white text-base font-medium">{t.author}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <i className="ri-map-pin-line text-gold text-xs" />
                    <p className="text-gold/65 text-xs tracking-wider">{t.location}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial CTA */}
        <div className="text-center py-12 border-t border-white/6">
          <p className="font-serif text-3xl md:text-4xl text-white font-light mb-3">
            Ready to start your success story?
          </p>
          <p className="text-warm-white/45 text-sm mb-8 max-w-lg mx-auto">
            Join hundreds of buyers, sellers, and investors who&apos;ve trusted Cam Kirkwood to navigate their Lake Tahoe real estate journey with expertise and care.
          </p>
          <a
            href="#consultation"
            className="inline-block whitespace-nowrap px-10 py-4.5 border border-gold/50 text-gold text-[11px] tracking-[0.25em] uppercase font-medium cursor-pointer rounded-md hover:bg-gold hover:text-white transition-all duration-300"
            style={{ paddingTop: '1.125rem', paddingBottom: '1.125rem' }}
          >
            Begin Your Journey
          </a>
        </div>
      </div>
    </section>
  );
}
